import { useCallback, useEffect, useRef, useState, type DragEvent } from "react";
import {
  AlertCircle,
  Camera,
  ChevronLeft,
  ChevronRight,
  ImagePlus,
  Loader2,
  RotateCw,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import type { JournalPhoto } from "@gb/contracts";
import { AuthedImage } from "@/components/gb/authed-file";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  photoContentPath,
  photoThumbnailPath,
  useCaptionPhoto,
  useDeletePhoto,
  useJournalPhotoUploads,
  useJournalPhotos,
  type PhotoUpload,
} from "./journal-photos-api";

/**
 * The day's photographs.
 *
 * Site work is pictures — the array, a damaged connector, a WhatsApp screenshot
 * of what the supplier promised — and until now a journal entry could hold none
 * of them. Each one is described by the same vision model that reads the images
 * inside a document, so a photograph is findable in the Brain by what is *in*
 * it rather than only by the day it was taken.
 *
 * Used by both the day's detail screen and its edit form, which is why it owns
 * its own queries rather than taking photos as a prop: the two screens would
 * otherwise each need the upload machine wired through them.
 */
export function JournalPhotos({
  projectId,
  entryId,
  readOnly,
  only,
  onFiled,
}: {
  projectId: string;
  entryId: string;
  /** A signed-off day. Photos stay visible; nothing can be added or removed. */
  readOnly?: boolean;
  /**
   * Show only these photographs, by id. Absent shows the day's whole set.
   *
   * The composer passes the ids it has uploaded in this sitting, because a note
   * being written is about *its* pictures — a day routinely already holds
   * others, filed from the phone or against an earlier note, and showing those
   * under a new note claims evidence it does not have.
   */
  only?: ReadonlySet<string>;
  /** Each photograph as it becomes a filed row. See `useJournalPhotoUploads`. */
  onFiled?: (photoId: string) => void;
}) {
  const { data: photos, isPending, isError, error, refetch } = useJournalPhotos(
    projectId,
    entryId,
  );
  const { uploads, add, retry, dismiss } = useJournalPhotoUploads(
    projectId,
    entryId,
    onFiled,
  );
  const remove = useDeletePhoto(projectId, entryId);
  const [lightboxAt, setLightboxAt] = useState<number | null>(null);

  const all = photos ?? [];
  const list = only ? all.filter((photo) => only.has(photo.id)) : all;

  if (isPending) {
    return (
      <div className="flex items-center gap-2 py-6 text-muted" role="status" aria-live="polite">
        <Loader2 size={15} className="animate-spin" />
        <span className="text-[0.86rem]">Loading photos…</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-input border-[1.5px] border-danger/40 bg-danger-bg px-4 py-3">
        <p className="text-[0.86rem] font-semibold text-danger">Couldn’t load the photos.</p>
        <p className="mt-1 text-[0.8rem] text-ink-slate">
          {error instanceof Error ? error.message : "Something went wrong."}
        </p>
        <Button variant="secondary" size="sm" className="mt-3" onClick={() => void refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {!readOnly && <PhotoDropzone onFiles={add} />}

      {list.length === 0 && uploads.length === 0 ? (
        <p className="text-[0.86rem] text-muted">
          {readOnly
            ? "No photos were filed for this day."
            : "No photos yet. Drop in what the site looked like — they’re read and filed in the Brain."}
        </p>
      ) : (
        <ul className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-3">
          {uploads.map((upload) => (
            <UploadTile
              key={upload.key}
              upload={upload}
              onRetry={() => retry(upload.key)}
              onDismiss={() => dismiss(upload.key)}
            />
          ))}
          {list.map((photo, index) => (
            <PhotoTile
              key={photo.id}
              photo={photo}
              projectId={projectId}
              entryId={entryId}
              onOpen={() => setLightboxAt(index)}
            />
          ))}
        </ul>
      )}

      {lightboxAt !== null && list[lightboxAt] && (
        <Lightbox
          photos={list}
          at={lightboxAt}
          projectId={projectId}
          entryId={entryId}
          readOnly={readOnly}
          onMove={setLightboxAt}
          onClose={() => setLightboxAt(null)}
          onDelete={async (photoId) => {
            await remove.mutateAsync(photoId);
            // Step back rather than close, so deleting the third of eight
            // leaves the reader looking at the fourth instead of at nothing.
            setLightboxAt((current) =>
              current === null ? null : list.length <= 1 ? null : Math.max(0, current - 1),
            );
          }}
        />
      )}
    </div>
  );
}

/**
 * The drop target.
 *
 * The drag counter is a ref, not a boolean: `dragleave` fires when the pointer
 * crosses into a *child* element, so a naive boolean flickers the highlight off
 * every time the cursor passes over the button inside the zone. Same fix as
 * the certificate upload's, and for the same reason.
 */
function PhotoDropzone({ onFiles }: { onFiles: (files: File[]) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);

  const take = (list: FileList | null): void => {
    const files = Array.from(list ?? []);
    if (files.length > 0) onFiles(files);
  };

  return (
    <div
      onDragEnter={(e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        dragDepth.current += 1;
        setDragging(true);
      }}
      onDragOver={(e: DragEvent<HTMLDivElement>) => e.preventDefault()}
      onDragLeave={(e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        dragDepth.current -= 1;
        if (dragDepth.current <= 0) setDragging(false);
      }}
      onDrop={(e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        dragDepth.current = 0;
        setDragging(false);
        take(e.dataTransfer.files);
      }}
      className={cn(
        "flex flex-wrap items-center gap-3 rounded-card border-2 border-dashed px-4 py-3 transition-colors",
        dragging ? "border-brand bg-brand-tint-3" : "border-border bg-surface-sunken",
      )}
    >
      <span className="flex shrink-0 text-muted">
        <Camera size={20} aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[0.92rem] font-bold text-ink">Drop photos here</p>
        <p className="mt-0.5 text-[0.8rem] text-muted">
          Site photos and screenshots — JPEG, PNG or WEBP. Each one is read and filed in
          the Brain.
        </p>
      </div>
      <Button
        size="sm"
        className="hover:bg-brand-deep"
        onClick={() => inputRef.current?.click()}
      >
        <ImagePlus size={15} />
        Add photos
      </Button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/png,image/jpeg,image/webp"
        className="sr-only"
        onChange={(e) => {
          take(e.target.files);
          // Reset, so picking the same file twice in a row still fires.
          e.target.value = "";
        }}
      />
    </div>
  );
}

function PhotoTile({
  photo,
  projectId,
  entryId,
  onOpen,
}: {
  photo: JournalPhoto;
  projectId: string;
  entryId: string;
  onOpen: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="group block w-full cursor-pointer overflow-hidden rounded-card border-[1.5px] border-border bg-surface text-left transition-colors hover:border-brand"
      >
        <span className="relative block aspect-4/3 bg-subtle">
          <AuthedImage
            path={photoThumbnailPath(projectId, entryId, photo.id)}
            alt={photo.caption ?? photo.description ?? photo.filename}
            className="size-full object-cover"
          />
          {photo.descriptionStatus === "pending" && (
            // Not a blocker on anything — the picture is already filed and
            // visible. This only says the description is still coming.
            <span
              className="absolute right-1.5 bottom-1.5 flex items-center gap-1 rounded-full bg-ink/75 px-2 py-[3px] text-[0.66rem] font-semibold text-white"
              title="Being read"
            >
              <Loader2 size={11} className="animate-spin" aria-hidden />
              Reading
            </span>
          )}
        </span>
        <span className="block px-2.5 py-2">
          <span className="block truncate text-[0.8rem] font-semibold text-ink">
            {photo.caption ?? photo.filename}
          </span>
          {photo.description && !photo.caption && (
            <span className="mt-0.5 block truncate text-[0.74rem] text-muted">
              {photo.description}
            </span>
          )}
        </span>
      </button>
    </li>
  );
}

/** A picture on its way up, shown from local bytes so the grid fills at once. */
function UploadTile({
  upload,
  onRetry,
  onDismiss,
}: {
  upload: PhotoUpload;
  onRetry: () => void;
  onDismiss: () => void;
}) {
  const failed = upload.status === "error";
  return (
    <li className="overflow-hidden rounded-card border-[1.5px] border-border bg-surface">
      <div className="relative aspect-4/3 bg-subtle">
        <img
          src={upload.objectUrl}
          alt=""
          className={cn("size-full object-cover", !failed && "opacity-60")}
        />
        {!failed && (
          <div className="absolute inset-x-2 bottom-2">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-ink/25">
              <div
                className="h-full rounded-full bg-brand transition-[width]"
                style={{ width: `${upload.progress}%` }}
              />
            </div>
          </div>
        )}
        {failed && (
          <span className="absolute inset-0 flex items-center justify-center bg-ink/55">
            <AlertCircle size={22} className="text-white" aria-hidden />
          </span>
        )}
      </div>
      <div className="px-2.5 py-2">
        <p className="truncate text-[0.8rem] font-semibold text-ink">{upload.filename}</p>
        {failed ? (
          <>
            <p className="mt-0.5 line-clamp-2 text-[0.72rem] font-semibold text-danger">
              {upload.error}
            </p>
            <div className="mt-1.5 flex items-center gap-1.5">
              {/* A refused file would be refused again (`architecture/0031`). */}
              {!upload.refused && (
                <Button variant="secondary" size="sm" onClick={onRetry}>
                  <RotateCw size={13} />
                  Retry
                </Button>
              )}
              <button
                type="button"
                onClick={onDismiss}
                aria-label={`Discard ${upload.filename}`}
                className="flex size-7 items-center justify-center rounded-control text-muted hover:bg-subtle hover:text-danger"
              >
                <X size={15} />
              </button>
            </div>
          </>
        ) : (
          <p className="mt-0.5 text-[0.72rem] text-muted">
            {upload.status === "processing" ? "Filing it…" : `${upload.progress}%`}
          </p>
        )}
      </div>
      <span aria-live="polite" className="sr-only">
        {failed ? `${upload.filename} failed: ${upload.error}` : ""}
      </span>
    </li>
  );
}

/**
 * One photograph, full size, with what the model saw in it.
 *
 * The description is shown rather than hidden behind a toggle: it is the thing
 * that makes the picture findable, and a person who can see what was written
 * about their photo can tell when it is wrong. Captioning is right there for
 * exactly that reason — the caption is the part a person owns, and the model
 * never writes it.
 */
function Lightbox({
  photos,
  at,
  projectId,
  entryId,
  readOnly,
  onMove,
  onClose,
  onDelete,
}: {
  photos: JournalPhoto[];
  at: number;
  projectId: string;
  entryId: string;
  readOnly?: boolean;
  onMove: (index: number) => void;
  onClose: () => void;
  onDelete: (photoId: string) => Promise<void>;
}) {
  const photo = photos[at]!;
  const caption = useCaptionPhoto(projectId, entryId);
  const [draft, setDraft] = useState(photo.caption ?? "");
  const [confirming, setConfirming] = useState(false);

  // Re-seeded when the reader moves to another picture, or the field would keep
  // showing the previous photo's caption over the new one.
  useEffect(() => {
    setDraft(photo.caption ?? "");
    setConfirming(false);
  }, [photo.id, photo.caption]);

  const move = useCallback(
    (delta: number) => {
      const next = at + delta;
      if (next >= 0 && next < photos.length) onMove(next);
    },
    [at, photos.length, onMove],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") move(-1);
      if (event.key === "ArrowRight") move(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [move, onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={photo.caption ?? photo.filename}
      className="fixed inset-0 z-50 flex flex-col bg-ink/60 p-3 sm:p-6"
      onClick={onClose}
    >
      {/* **One surface, not two pieces on a backdrop.** The picture and its
          reading belong to the same object, so they sit in one bordered white
          card split by a divider — the same shape the answer panel uses. A
          floating image over a dim wash read as two unrelated things that
          happened to be on screen together. */}
      <div
        className="mx-auto flex min-h-0 w-full max-w-[1500px] flex-1 flex-col overflow-hidden rounded-card-lg border-[1.5px] border-border bg-surface"
        // The chrome is inside the backdrop, so clicks on it must not close.
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-5 py-3">
          <span className="truncate font-display text-[0.8rem] font-semibold tracking-[0.06em] text-ink uppercase">
            {photo.kind === "screenshot" ? "Screenshot" : "Site photo"} · {at + 1} of{" "}
            {photos.length}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex size-9 items-center justify-center rounded-control text-muted transition-colors hover:bg-subtle hover:text-ink"
          >
            <X size={20} />
          </button>
        </div>

        {/* Half the screen each, side by side — the picture and what was read
            out of it are meant to be compared, and a description stacked under
            a tall screenshot means scrolling away from the thing it describes.
            Stacked only below `lg`, where two columns would leave both
            unreadable. */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
          {/* A neutral mat behind the picture. White-on-white loses the edge of
              a scanned page, and a photograph with light borders bleeds into
              the card. */}
          <div className="flex min-h-0 flex-1 items-center gap-1 overflow-hidden border-b border-border bg-subtle/60 p-4 lg:basis-1/2 lg:border-r lg:border-b-0">
            <NavButton
              direction="previous"
              disabled={at === 0}
              onClick={() => move(-1)}
            />
            {/* `max-h-full` is load-bearing: `flex-1` sizes the width but leaves
                the height to the image's natural size, so a portrait screenshot
                spills out of the dialog and behind the caption bar. `object-
                contain` only fits the picture inside the box it is *given*. */}
            <AuthedImage
              path={photoContentPath(projectId, entryId, photo.id)}
              alt={photo.caption ?? photo.description ?? photo.filename}
              className="max-h-full min-h-0 flex-1 object-contain"
              fallbackClassName="h-full w-full rounded-card-lg"
            />
            <NavButton
              direction="next"
              disabled={at === photos.length - 1}
              onClick={() => move(1)}
            />
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-5 lg:basis-1/2">
          {/* **Signed off means read, not filled in.** A day that cannot be
              edited used to render the same empty text field with the same
              inviting placeholder and no buttons beside it — a control that
              looks like it wants an answer and refuses one. Read-only shows the
              caption as what it is: a line of text, or an honest absence. */}
          {readOnly ? (
            <div>
              <span className="mb-1 block font-display text-[0.68rem] font-semibold tracking-[0.06em] text-muted uppercase">
                Caption
              </span>
              {photo.caption ? (
                <p className="text-[0.92rem] text-ink">{photo.caption}</p>
              ) : (
                <p className="text-[0.9rem] text-muted">
                  No caption. This day is signed off, so it can’t be added now.
                </p>
              )}
            </div>
          ) : (
          <div className="flex flex-wrap items-end gap-2">
            <label className="min-w-[220px] flex-1">
              <span className="mb-1 block font-display text-[0.68rem] font-semibold tracking-[0.06em] text-muted uppercase">
                Caption
              </span>
              <Input
                value={draft}
                placeholder="What is this showing?"
                onChange={(e) => setDraft(e.target.value)}
              />
            </label>
            {
              <>
                <Button
                  size="sm"
                  className="hover:bg-brand-deep"
                  disabled={caption.isPending || draft === (photo.caption ?? "")}
                  onClick={() =>
                    caption.mutate({ photoId: photo.id, caption: draft.trim() || null })
                  }
                >
                  {caption.isPending ? "Saving…" : "Save caption"}
                </Button>
                {confirming ? (
                  <span className="flex items-center gap-1.5">
                    <span className="text-[0.78rem] font-semibold text-danger">Delete?</span>
                    <Button variant="secondary" size="sm" onClick={() => setConfirming(false)}>
                      No
                    </Button>
                    <Button
                      size="sm"
                      className="bg-danger hover:bg-danger"
                      onClick={() => void onDelete(photo.id)}
                    >
                      Yes
                    </Button>
                  </span>
                ) : (
                  <Button variant="secondary" size="sm" onClick={() => setConfirming(true)}>
                    <Trash2 size={15} />
                    Delete
                  </Button>
                )}
              </>
            }
          </div>
          )}

          {caption.error && (
            <p role="alert" className="mt-2 text-[0.8rem] font-semibold text-danger">
              {caption.error.message}
            </p>
          )}

          <ModelReading photo={photo} />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * What a model saw, labelled as such.
 *
 * Attributed rather than presented as fact, because it is neither the
 * photographer's words nor a certainty — and because this text is what the
 * Brain will search and quote, so somebody looking at the picture is the only
 * person who can notice it is wrong.
 */
function ModelReading({ photo }: { photo: JournalPhoto }) {
  if (photo.descriptionStatus === "pending") {
    return (
      <p
        className="mt-3 flex items-center gap-1.5 text-[0.82rem] text-muted"
        role="status"
        aria-live="polite"
      >
        <Loader2 size={13} className="animate-spin" aria-hidden />
        Reading this photo…
      </p>
    );
  }

  if (!photo.description && !photo.visibleText) {
    return (
      <p className="mt-3 text-[0.82rem] text-muted">
        {photo.descriptionStatus === "failed"
          ? "This photo couldn’t be read. It is filed and searchable by its caption."
          : "No description — it is filed and searchable by its caption."}
      </p>
    );
  }

  return (
    <div className="mt-3 border-t border-border-inner pt-3">
      <p className="flex items-center gap-1.5 font-display text-[0.68rem] font-semibold tracking-[0.06em] text-muted uppercase">
        <Sparkles size={12} aria-hidden />
        What the Brain read
      </p>
      {photo.description && (
        <p className="mt-1.5 text-[0.86rem] leading-[1.5] text-ink-prose">
          {photo.description}
        </p>
      )}
      {photo.visibleText && (
        <>
          <p className="mt-2.5 font-display text-[0.66rem] font-semibold tracking-[0.06em] text-muted uppercase">
            Text in the image
          </p>
          <p className="mt-1 text-[0.82rem] leading-[1.5] whitespace-pre-wrap text-ink-prose">
            {photo.visibleText}
          </p>
        </>
      )}
    </div>
  );
}

function NavButton({
  direction,
  disabled,
  onClick,
}: {
  direction: "previous" | "next";
  disabled: boolean;
  onClick: () => void;
}) {
  const Icon = direction === "previous" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={`${direction === "previous" ? "Previous" : "Next"} photo`}
      className="flex size-10 shrink-0 items-center justify-center rounded-full text-muted transition-colors hover:bg-surface hover:text-ink disabled:pointer-events-none disabled:opacity-25"
    >
      <Icon size={24} />
    </button>
  );
}
